import React, { useState, useEffect } from 'react';
import { Layout, Menu, theme, Typography, Space, Avatar, Button, Card, Row, Col, Statistic, Badge, Tabs, Table, Input, Tag, Select, DatePicker, Dropdown, Spin, message, Progress, Tooltip, Modal, Form, InputNumber, Divider, Empty } from 'antd';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import UserProfile from './UserProfile';
import { analyticsAPI } from '../services/api';
import {
  DashboardOutlined,
  UserOutlined,
  StarOutlined,
  RiseOutlined,
  SettingOutlined,
  CrownOutlined,
  ExclamationCircleOutlined,
  MenuOutlined,
  MessageOutlined,
  ShareAltOutlined,
  CheckCircleFilled,
  DownOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserAddOutlined,
  MailOutlined,
  LinkOutlined,
  BarChartOutlined,
  GlobalOutlined,
  SearchOutlined,
  GiftOutlined,
  BankOutlined,
  DollarOutlined,
  PlusOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CalculatorOutlined
} from '@ant-design/icons';
import './ReferralAnalytics.css';
import '../styles/sidebar-standard.css';
import '../styles/menu-hover-overrides.css';
import {
  REFERRAL_TIER_COUNT,
  paidRecognitionTiersUnlockedCount,
  formatMilestoneSummary,
  recognitionLabelForThreshold,
  milestoneToPaidFriendsThreshold,
} from '../constants/referralRewards';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const ReferralAnalytics: React.FC = () => {
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('1 Month');
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState<[string, string]>(['', '']);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [invitationsData, setInvitationsData] = useState<any[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [invitationStatusFilter, setInvitationStatusFilter] = useState<string>('all');
  const [invitationSearchText, setInvitationSearchText] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [donorsData, setDonorsData] = useState<any[]>([]);
  const [donorsLoading, setDonorsLoading] = useState(false);
  const [donorSearchText, setDonorSearchText] = useState<string>('');
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [creditGrantModalVisible, setCreditGrantModalVisible] = useState(false);
  const [selectedDonor, setSelectedDonor] = useState<any>(null);
  const [creditGrantForm] = Form.useForm();
  const navigate = useNavigate();

  // Test data for analytics
  const getTestAnalyticsData = () => {
    return {
      totalReferrals: 1250,
      activeReferrers: 45,
      conversionRate: 71.2,
      topReferrers: [
        {
          user_id: 101,
          name: 'Sarah Johnson',
          email: 'sarah.johnson@example.com',
          total_referrals: 45,
          successful_referrals: 32,
          conversion_rate: 71.1,
          points_earned: 3200,
          total_value: 1600.00
        },
        {
          user_id: 102,
          name: 'Michael Chen',
          email: 'michael.chen@example.com',
          total_referrals: 38,
          successful_referrals: 28,
          conversion_rate: 73.7,
          points_earned: 2800,
          total_value: 1400.00
        },
        {
          user_id: 103,
          name: 'Emily Rodriguez',
          email: 'emily.r@example.com',
          total_referrals: 32,
          successful_referrals: 24,
          conversion_rate: 75.0,
          points_earned: 2400,
          total_value: 1200.00
        },
        {
          user_id: 104,
          name: 'David Kim',
          email: 'david.kim@example.com',
          total_referrals: 28,
          successful_referrals: 20,
          conversion_rate: 71.4,
          points_earned: 2000,
          total_value: 1000.00
        },
        {
          user_id: 105,
          name: 'Jessica Martinez',
          email: 'jessica.m@example.com',
          total_referrals: 25,
          successful_referrals: 18,
          conversion_rate: 72.0,
          points_earned: 1800,
          total_value: 900.00
        },
        {
          user_id: 106,
          name: 'Robert Williams',
          email: 'robert.w@example.com',
          total_referrals: 22,
          successful_referrals: 16,
          conversion_rate: 72.7,
          points_earned: 1600,
          total_value: 800.00
        },
        {
          user_id: 107,
          name: 'Amanda Brown',
          email: 'amanda.b@example.com',
          total_referrals: 20,
          successful_referrals: 15,
          conversion_rate: 75.0,
          points_earned: 1500,
          total_value: 750.00
        },
        {
          user_id: 108,
          name: 'James Davis',
          email: 'james.d@example.com',
          total_referrals: 18,
          successful_referrals: 13,
          conversion_rate: 72.2,
          points_earned: 1300,
          total_value: 650.00
        }
      ],
      referralSources: [
        { name: 'Social Media', count: 450 },
        { name: 'Email', count: 380 },
        { name: 'Direct', count: 220 },
        { name: 'QR Code', count: 150 },
        { name: 'Word of Mouth', count: 50 }
      ],
      monthlyTrends: [
        { month: '2024-01', referrals: 150, successful: 110, revenue: 5500.00 },
        { month: '2024-02', referrals: 180, successful: 130, revenue: 6500.00 },
        { month: '2024-03', referrals: 200, successful: 145, revenue: 7250.00 },
        { month: '2024-04', referrals: 220, successful: 160, revenue: 8000.00 },
        { month: '2024-05', referrals: 250, successful: 180, revenue: 9000.00 },
        { month: '2024-06', referrals: 250, successful: 175, revenue: 8750.00 }
      ]
    };
  };

  const getSelectedPeriod = () => {
    switch (selectedTimeFilter) {
      case 'All':
        return 'all';
      case '1 Week':
        return '7d';
      case '15 Days':
        return '15d';
      case '1 Month':
        return '30d';
      case '3 Months':
        return '90d';
      case '6 Months':
        return '180d';
      case 'One Year':
        return '365d';
      default:
        return '30d';
    }
  };

  // Load referral analytics from API - real data only, no dummy fallback
  const loadReferralAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const selectedPeriod = getSelectedPeriod();
      const response = await analyticsAPI.getReferralAnalytics(selectedPeriod);
      
      if (response.success && response.data) {
        setAnalyticsData(response.data);
      } else {
        setAnalyticsData({ totalReferrals: 0, activeReferrers: 0, conversionRate: 0, topReferrers: [], referralSources: [], monthlyTrends: [] });
      }
    } catch (err: any) {
      console.error('Error loading referral analytics:', err);
      setError(err?.message || 'Failed to load referral analytics');
      setAnalyticsData({ totalReferrals: 0, activeReferrers: 0, conversionRate: 0, topReferrers: [], referralSources: [], monthlyTrends: [] });
    } finally {
      setLoading(false);
    }
  };

  // Test data for demonstration
  const getTestInvitationsData = () => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(now);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const twoWeeksAgo = new Date(now);
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    const lastMonth = new Date(now);
    lastMonth.setDate(lastMonth.getDate() - 30);
    const twoMonthsAgo = new Date(now);
    twoMonthsAgo.setDate(twoMonthsAgo.getDate() - 60);

    return [
      {
        id: 1,
        referrer_id: 101,
        referrer_name: 'Sarah Johnson',
        referrer_email: 'sarah.johnson@example.com',
        email: 'friend1@example.com',
        referral_code: 'SARAH2024',
        referral_link: 'https://app.com/invite/SARAH2024',
        status: 'pending',
        created_at: yesterday.toISOString(),
        signed_up_at: null,
        paid_at: null,
        cancelled_at: null
      },
      {
        id: 2,
        referrer_id: 102,
        referrer_name: 'Michael Chen',
        referrer_email: 'michael.chen@example.com',
        email: 'colleague@example.com',
        referral_code: 'MICHAEL123',
        referral_link: 'https://app.com/invite/MICHAEL123',
        status: 'signed_up',
        created_at: lastWeek.toISOString(),
        signed_up_at: yesterday.toISOString(),
        paid_at: null,
        cancelled_at: null
      },
      {
        id: 3,
        referrer_id: 101,
        referrer_name: 'Sarah Johnson',
        referrer_email: 'sarah.johnson@example.com',
        email: 'friend2@example.com',
        referral_code: 'SARAH2024B',
        referral_link: 'https://app.com/invite/SARAH2024B',
        status: 'paid',
        created_at: twoWeeksAgo.toISOString(),
        signed_up_at: lastWeek.toISOString(),
        paid_at: yesterday.toISOString(),
        cancelled_at: null
      },
      {
        id: 4,
        referrer_id: 103,
        referrer_name: 'Emily Rodriguez',
        referrer_email: 'emily.r@example.com',
        email: 'family@example.com',
        referral_code: 'EMILY456',
        referral_link: 'https://app.com/invite/EMILY456',
        status: 'payment_setup',
        created_at: lastWeek.toISOString(),
        signed_up_at: yesterday.toISOString(),
        paid_at: null,
        cancelled_at: null
      },
      {
        id: 5,
        referrer_id: 102,
        referrer_name: 'Michael Chen',
        referrer_email: 'michael.chen@example.com',
        email: 'oldfriend@example.com',
        referral_code: 'MICHAEL789',
        referral_link: 'https://app.com/invite/MICHAEL789',
        status: 'cancelled',
        created_at: lastMonth.toISOString(),
        signed_up_at: null,
        paid_at: null,
        cancelled_at: lastWeek.toISOString()
      },
      {
        id: 6,
        referrer_id: 104,
        referrer_name: 'David Kim',
        referrer_email: 'david.kim@example.com',
        email: 'teammate@example.com',
        referral_code: 'DAVID2024',
        referral_link: 'https://app.com/invite/DAVID2024',
        status: 'pending',
        created_at: yesterday.toISOString(),
        signed_up_at: null,
        paid_at: null,
        cancelled_at: null
      },
      {
        id: 7,
        referrer_id: 101,
        referrer_name: 'Sarah Johnson',
        referrer_email: 'sarah.johnson@example.com',
        email: 'friend3@example.com',
        referral_code: 'SARAH2024C',
        referral_link: 'https://app.com/invite/SARAH2024C',
        status: 'paid',
        created_at: twoWeeksAgo.toISOString(),
        signed_up_at: lastWeek.toISOString(),
        paid_at: yesterday.toISOString(),
        cancelled_at: null
      },
      {
        id: 8,
        referrer_id: 105,
        referrer_name: 'Jessica Martinez',
        referrer_email: 'jessica.m@example.com',
        email: 'neighbor@example.com',
        referral_code: 'JESSICA999',
        referral_link: 'https://app.com/invite/JESSICA999',
        status: 'signed_up',
        created_at: lastWeek.toISOString(),
        signed_up_at: now.toISOString(),
        paid_at: null,
        cancelled_at: null
      },
      {
        id: 9,
        referrer_id: 103,
        referrer_name: 'Emily Rodriguez',
        referrer_email: 'emily.r@example.com',
        email: 'coworker@example.com',
        referral_code: 'EMILY321',
        referral_link: 'https://app.com/invite/EMILY321',
        status: 'pending',
        created_at: twoWeeksAgo.toISOString(),
        signed_up_at: null,
        paid_at: null,
        cancelled_at: null
      },
      {
        id: 10,
        referrer_id: 102,
        referrer_name: 'Michael Chen',
        referrer_email: 'michael.chen@example.com',
        email: 'classmate@example.com',
        referral_code: 'MICHAEL555',
        referral_link: 'https://app.com/invite/MICHAEL555',
        status: 'paid',
        created_at: lastMonth.toISOString(),
        signed_up_at: twoWeeksAgo.toISOString(),
        paid_at: lastWeek.toISOString(),
        cancelled_at: null
      },
      {
        id: 11,
        referrer_id: 101,
        referrer_name: 'Sarah Johnson',
        referrer_email: 'sarah.johnson@example.com',
        email: 'friend4@example.com',
        referral_code: 'SARAH2024D',
        referral_link: 'https://app.com/invite/SARAH2024D',
        status: 'pending',
        created_at: yesterday.toISOString(),
        signed_up_at: null,
        paid_at: null,
        cancelled_at: null
      },
      {
        id: 12,
        referrer_id: 104,
        referrer_name: 'David Kim',
        referrer_email: 'david.kim@example.com',
        email: 'buddy@example.com',
        referral_code: 'DAVID777',
        referral_link: 'https://app.com/invite/DAVID777',
        status: 'cancelled',
        created_at: twoMonthsAgo.toISOString(),
        signed_up_at: null,
        paid_at: null,
        cancelled_at: lastWeek.toISOString()
      }
    ];
  };

  // Build invitations from donors with referrals (real data)
  const buildInvitationsFromDonors = (donors: any[]): any[] => {
    const invitations: any[] = [];
    donors.forEach((donor) => {
      const refs = donor.referrals || donor.referral_stats?.referrals || [];
      refs.forEach((r: any) => {
        invitations.push({
          id: r.id,
          referrer_id: donor.user_id || donor.id,
          referrer_name: donor.name || donor.email?.split('@')[0],
          referrer_email: donor.email,
          email: r.email || r.referred_email,
          referral_code: r.code || r.referral_token,
          referral_link: `${typeof window !== 'undefined' ? window.location.origin : ''}/signup?ref=${donor.user_id || donor.id}`,
          status: r.status || 'pending',
          created_at: r.created_at,
          signed_up_at: r.first_payment_at ? r.created_at : null,
          paid_at: r.first_payment_at,
          cancelled_at: null
        });
      });
    });
    return invitations;
  };

  // Load invitations data - use real API or derive from donors with referrals
  const loadInvitations = async () => {
    setInvitationsLoading(true);
    
    try {
      const status = invitationStatusFilter !== 'all' ? invitationStatusFilter : undefined;
      const response = await analyticsAPI.getReferralInvitations('30d', status);
      
      if (response.success && response.data) {
        const invitations = Array.isArray(response.data) 
          ? response.data 
          : response.data.invitations || [];
        setInvitationsData(invitations);
      } else if (donorsData.length > 0) {
        setInvitationsData(buildInvitationsFromDonors(donorsData));
      } else {
        setInvitationsData([]);
      }
    } catch (error) {
      console.error('Error loading referral invitations:', error);
      if (donorsData.length > 0) {
        setInvitationsData(buildInvitationsFromDonors(donorsData));
      } else {
        setInvitationsData([]);
      }
    } finally {
      setInvitationsLoading(false);
    }
  };

  // Load all donors with referral data
  const loadAllDonors = async () => {
    setDonorsLoading(true);
    try {
      const response = await analyticsAPI.getAllDonorsWithReferrals();
      if (response.success && response.data) {
        setDonorsData(response.data);
      } else {
        // Use test data if API doesn't return data
        setDonorsData([]);
      }
    } catch (error) {
      console.error('Error loading donors:', error);
      setDonorsData([]);
    } finally {
      setDonorsLoading(false);
    }
  };

  // Handle credit grant
  const handleCreditGrant = async (values: any) => {
    if (!selectedDonor) return;
    
    try {
      await analyticsAPI.grantCredit(
        selectedDonor.user_id || selectedDonor.id,
        values.amount,
        values.description,
        values.expiresInDays
      );
      message.success(`Successfully granted $${values.amount} account credit to ${selectedDonor.name}`);
      setCreditGrantModalVisible(false);
      creditGrantForm.resetFields();
      setSelectedDonor(null);
      // Reload donors to refresh credit data
      await loadAllDonors();
    } catch (error: any) {
      message.error(error.message || 'Failed to grant credit');
    }
  };

  // Load data on component mount - real data only
  useEffect(() => {
    loadReferralAnalytics();
    loadAllDonors();
  }, []);

  // Load invitations when donors data is available or filter changes
  useEffect(() => {
    loadInvitations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donorsData, invitationStatusFilter]);

  const handleTimeFilterChange = ({ key }: { key: string }) => {
    if (key === 'Custom Date') {
      message.info('Custom date range is not supported yet.');
      return;
    }
    setSelectedTimeFilter(key);
  };

  useEffect(() => {
    loadReferralAnalytics();
  }, [selectedTimeFilter]);

  const timeFilterMenu = (
    <Menu onClick={handleTimeFilterChange}>
      <Menu.Item key="All" icon={<CheckCircleFilled style={{ color: '#DB8633' }} />}>
        All
      </Menu.Item>
      <Menu.Item key="1 Week">1 Week</Menu.Item>
      <Menu.Item key="15 Days">15 Days</Menu.Item>
      <Menu.Item key="1 Month">1 Month</Menu.Item>
      <Menu.Item key="3 Months">3 Months</Menu.Item>
      <Menu.Item key="6 Months">6 Months</Menu.Item>
      <Menu.Item key="One Year">One Year</Menu.Item>
      <Menu.Item key="Custom Date">Custom Date</Menu.Item>
    </Menu>
  );

  // Referral Overview Data - real data only, no dummy growth percentages
  const referralOverviewData = [
    { 
      title: 'Total Referrals', 
      value: analyticsData?.totalReferrals ?? '--', 
      icon: <TeamOutlined />, 
      color: '#DB8633' 
    },
    { 
      title: 'Active Referrers', 
      value: analyticsData?.activeReferrers ?? '--', 
      icon: <CheckCircleFilled />, 
      color: '#324E58' 
    },
    { 
      title: 'Conversion Rate', 
      value: analyticsData?.conversionRate != null ? `${analyticsData.conversionRate}%` : '--', 
      icon: <BarChartOutlined />, 
      color: '#324E58' 
    },
    { 
      title: 'Top Referrer', 
      value: analyticsData?.topReferrers?.[0]?.name || '--', 
      icon: <TrophyOutlined />, 
      color: '#324E58' 
    },
    {
      title: 'Social Media Referrals',
      value: analyticsData?.referralSources?.find((s: any) => s.name === 'Social Media')?.count ?? '--',
      icon: <ShareAltOutlined />,
      color: '#DB8633'
    },
    {
      title: 'Email Referrals',
      value: analyticsData?.referralSources?.find((s: any) => s.name === 'Email')?.count ?? '--',
      icon: <MessageOutlined />,
      color: '#324E58'
    },
  ];

  // Calculate invitation statistics from data
  const invitationStats = {
    pending: invitationsData.filter((inv: any) => inv.status === 'pending').length,
    accepted: invitationsData.filter((inv: any) => inv.status === 'signed_up' || inv.status === 'payment_setup' || inv.status === 'paid').length,
    expired: invitationsData.filter((inv: any) => {
      if (inv.status === 'cancelled') return true;
      // Check if invitation is older than 30 days and still pending
      if (inv.status === 'pending' && inv.created_at) {
        const createdDate = new Date(inv.created_at);
        const daysSince = (Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24);
        return daysSince > 30;
      }
      return false;
    }).length,
    acceptedToday: invitationsData.filter((inv: any) => {
      if (inv.signed_up_at) {
        const signupDate = new Date(inv.signed_up_at);
        const today = new Date();
        return signupDate.toDateString() === today.toDateString();
      }
      return false;
    }).length,
    expiredThisWeek: invitationsData.filter((inv: any) => {
      if (inv.status === 'cancelled' && inv.cancelled_at) {
        const cancelledDate = new Date(inv.cancelled_at);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return cancelledDate >= weekAgo;
      }
      return false;
    }).length
  };

  // Filter invitations based on search and status
  const filteredInvitations = invitationsData.filter((inv: any) => {
    const matchesStatus = invitationStatusFilter === 'all' || inv.status === invitationStatusFilter;
    const matchesSearch = !invitationSearchText || 
      (inv.email && inv.email.toLowerCase().includes(invitationSearchText.toLowerCase())) ||
      (inv.referral_code && inv.referral_code.toLowerCase().includes(invitationSearchText.toLowerCase())) ||
      (inv.referrer_name && inv.referrer_name.toLowerCase().includes(invitationSearchText.toLowerCase()));
    return matchesStatus && matchesSearch;
  });

  // Invitation table columns
  const invitationColumns = [
    {
      title: 'Referrer',
      dataIndex: 'referrer_name',
      key: 'referrer_name',
      width: 180,
      fixed: 'left' as const,
      render: (text: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Avatar size={32} style={{ backgroundColor: '#DB8633' }}>
            {text ? text.split(' ').map((n: string) => n[0]).join('').toUpperCase() : 'U'}
          </Avatar>
          <div>
            <Text strong style={{ fontSize: '14px' }}>{text || 'Unknown'}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '11px' }}>{record.referrer_email || ''}</Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Invited Email',
      dataIndex: 'email',
      key: 'email',
      width: 180,
      render: (email: string) => <Text style={{ fontSize: '13px' }}>{email || 'N/A'}</Text>,
    },
    {
      title: 'Referral Code',
      dataIndex: 'referral_code',
      key: 'referral_code',
      width: 120,
      render: (code: string) => (
        <Text code style={{ fontSize: '12px' }}>{code || 'N/A'}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const statusConfig: any = {
          pending: { color: 'orange', text: 'Pending' },
          signed_up: { color: 'blue', text: 'Signed Up' },
          payment_setup: { color: 'cyan', text: 'Payment Setup' },
          paid: { color: 'green', text: 'Paid' },
          cancelled: { color: 'red', text: 'Cancelled' },
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: (date: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {date ? new Date(date).toLocaleDateString() : 'N/A'}
        </Text>
      ),
    },
    {
      title: 'Accepted',
      dataIndex: 'signed_up_at',
      key: 'signed_up_at',
      width: 100,
      render: (date: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {date ? new Date(date).toLocaleDateString() : '-'}
        </Text>
      ),
    },
    {
      title: 'Paid',
      dataIndex: 'paid_at',
      key: 'paid_at',
      width: 100,
      render: (date: string) => (
        <Text type={date ? 'success' : 'secondary'} style={{ fontSize: '12px' }}>
          {date ? new Date(date).toLocaleDateString() : '-'}
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      align: 'center' as const,
      render: (_: any, record: any) => (
        <Space size="small" style={{ display: 'flex', justifyContent: 'center' }}>
          {record.status === 'pending' && (
            <Tooltip title="Resend Invitation">
              <Button 
                type="default"
                shape="circle"
                size="middle"
                icon={<MailOutlined />}
                onClick={() => handleResendInvitation(record)}
                style={{ 
                  backgroundColor: '#fff7e6',
                  borderColor: '#DB8633',
                  color: '#DB8633',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#DB8633';
                  e.currentTarget.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff7e6';
                  e.currentTarget.style.color = '#DB8633';
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="Copy Referral Link">
            <Button 
              type="default"
              shape="circle"
              size="middle"
              icon={<LinkOutlined />}
              onClick={() => handleCopyReferralLink(record)}
              style={{ 
                backgroundColor: '#f0f9ff',
                borderColor: '#DB8633',
                color: '#DB8633',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#DB8633';
                e.currentTarget.style.color = '#ffffff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f9ff';
                e.currentTarget.style.color = '#DB8633';
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Generate Referral modal state
  const [generateReferralModalVisible, setGenerateReferralModalVisible] = useState(false);
  const [resendPendingLoading, setResendPendingLoading] = useState(false);
  const [sendInviteModalVisible, setSendInviteModalVisible] = useState(false);
  const [sendInviteLoading, setSendInviteLoading] = useState(false);
  const [sendInviteForm] = Form.useForm();

  // Handler functions
  const handleResendInvitation = async (invitation: any) => {
    try {
      message.loading({ content: `Resending invitation to ${invitation.email || 'user'}...`, key: 'resend' });
      const response = await analyticsAPI.resendReferralInvitation(invitation.id || invitation.referrer_id);
      if (response?.success) {
        message.success({ content: 'Invitation resent successfully!', key: 'resend' });
        loadInvitations();
      } else {
        message.error({ content: response?.error || 'Failed to resend invitation', key: 'resend' });
      }
    } catch (err: any) {
      message.error({ content: err?.message || 'Failed to resend invitation', key: 'resend' });
    }
  };

  const handleCopyReferralLink = (invitation: any) => {
    const link = invitation.referral_link || `${typeof window !== 'undefined' ? window.location.origin : ''}/signup?ref=${invitation.referrer_id || invitation.referral_code}`;
    navigator.clipboard.writeText(link).then(() => {
      message.success('Referral link copied to clipboard!');
    }).catch(() => {
      message.error('Failed to copy link');
    });
  };

  const handleSendNewInvitations = () => {
    setSendInviteModalVisible(true);
  };

  const handleSendInviteSubmit = async () => {
    try {
      const values = await sendInviteForm.validateFields();
      setSendInviteLoading(true);
      const emails: string[] = values.emails
        .split(/[\n,;]+/)
        .map((e: string) => e.trim())
        .filter(Boolean);
      if (emails.length === 0) {
        message.error('Please enter at least one email address');
        return;
      }
      const response = await analyticsAPI.sendReferralInvitationsByEmail(emails);
      if (response?.success) {
        message.success(`Invitation${emails.length > 1 ? 's' : ''} sent to ${emails.length} recipient${emails.length > 1 ? 's' : ''}!`);
      } else {
        message.warning(response?.error || response?.message || 'Some invitations could not be sent.');
      }
      sendInviteForm.resetFields();
      setSendInviteModalVisible(false);
      loadInvitations();
    } catch {
      // validation error — stay open
    } finally {
      setSendInviteLoading(false);
    }
  };

  const handleResendPending = async () => {
    const pendingInvitations = invitationsData.filter((inv: any) => inv.status === 'pending');
    if (pendingInvitations.length === 0) {
      message.warning('No pending invitations to resend');
      return;
    }
    setResendPendingLoading(true);
    try {
      const ids = pendingInvitations.map((inv: any) => inv.id).filter(Boolean);
      if (ids.length === 0) {
        message.warning('Pending invitations do not support resend yet. Backend support may be required.');
        return;
      }
      const response = await analyticsAPI.resendReferralInvitations(ids);
      if (response?.success) {
        message.success(`Resent ${ids.length} pending invitation(s) successfully!`);
        loadInvitations();
      } else {
        message.error(response?.error || 'Failed to resend invitations');
      }
    } catch (err: any) {
      message.error(err?.message || 'Failed to resend invitations. Backend support may be required.');
    } finally {
      setResendPendingLoading(false);
    }
  };

  const handleGenerateReferralLinks = () => {
    setGenerateReferralModalVisible(true);
  };

  // Open credit grant modal
  const openCreditGrantModal = (donor: any) => {
    setSelectedDonor(donor);
    setCreditGrantModalVisible(true);
  };

  // Filter donors based on search
  const filteredDonors = donorsData.filter((donor: any) => {
    if (!donorSearchText) return true;
    const searchLower = donorSearchText.toLowerCase();
    return (
      (donor.name && donor.name.toLowerCase().includes(searchLower)) ||
      (donor.email && donor.email.toLowerCase().includes(searchLower)) ||
      (donor.user_id && donor.user_id.toString().includes(searchLower))
    );
  });

  const referralColumns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 100,
      render: (rank: number) => {
        const getRankStyle = () => {
          if (rank === 1) {
            return {
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              color: '#ffffff',
              border: '2px solid #FFD700',
              boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)'
            };
          } else if (rank === 2) {
            return {
              background: 'linear-gradient(135deg, #C0C0C0 0%, #A0A0A0 100%)',
              color: '#ffffff',
              border: '2px solid #C0C0C0',
              boxShadow: '0 4px 12px rgba(192, 192, 192, 0.3)'
            };
          } else if (rank === 3) {
            return {
              background: 'linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)',
              color: '#ffffff',
              border: '2px solid #CD7F32',
              boxShadow: '0 4px 12px rgba(205, 127, 50, 0.3)'
            };
          } else {
            return {
              background: '#f5f5f5',
              color: '#324E58',
              border: '2px solid #e8e8e8'
            };
          }
        };
        
        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            width: '60px',
            height: '60px',
            borderRadius: '12px',
            ...getRankStyle(),
            fontWeight: 'bold',
            fontSize: '18px',
            position: 'relative'
          }}>
            {rank === 1 && <CrownOutlined style={{ fontSize: '20px' }} />}
            {(rank === 2 || rank === 3) && <TrophyOutlined style={{ fontSize: '18px' }} />}
            <span>{rank}</span>
          </div>
        );
      },
    },
    {
      title: 'Referrer',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      render: (text: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Avatar 
            size={48} 
            style={{ 
              backgroundColor: '#DB8633',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            {record.avatar}
          </Avatar>
          <div>
            <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: '4px' }}>
              {text}
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.email}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Total Referrals',
      dataIndex: 'referrals',
      key: 'referrals',
      width: 140,
      align: 'center' as const,
      render: (referrals: number) => (
        <div style={{ textAlign: 'center' }}>
          <Text strong style={{ fontSize: '20px', color: '#324E58', display: 'block' }}>
            {referrals}
          </Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>referrals</Text>
        </div>
      ),
    },
    {
      title: 'Paid referrals',
      dataIndex: 'successful',
      key: 'successful',
      width: 140,
      align: 'center' as const,
      render: (successful: number) => (
        <div style={{ textAlign: 'center' }}>
          <Text strong style={{ fontSize: '20px', color: '#52c41a', display: 'block' }}>
            {successful}
          </Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>paid</Text>
        </div>
      ),
    },
    {
      title: 'Conversion Rate',
      dataIndex: 'conversionRate',
      key: 'conversionRate',
      width: 140,
      align: 'center' as const,
      render: (rate: string) => (
        <div style={{
          display: 'inline-block',
          padding: '6px 16px',
          borderRadius: '20px',
          background: 'linear-gradient(135deg, #DB8633 0%, #ff9a56 100%)',
          color: '#ffffff',
          fontWeight: '600',
          fontSize: '14px',
          boxShadow: '0 2px 8px rgba(219, 134, 51, 0.2)'
        }}>
          {rate}
        </div>
      ),
    },
    {
      title: 'Recognition tiers',
      dataIndex: 'recognitionTiersUnlocked',
      key: 'recognitionTiersUnlocked',
      width: 160,
      align: 'center' as const,
      render: (n: number) => (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '8px 12px',
          background: '#fff7e6',
          borderRadius: '8px',
          border: '1px solid #ffe7ba'
        }}>
          <TrophyOutlined style={{ color: '#DB8633', fontSize: '18px' }} />
          <Text strong style={{ fontSize: '16px', color: '#DB8633' }}>
            {n}/{REFERRAL_TIER_COUNT}
          </Text>
        </div>
      ),
    },
    {
      title: 'Total Value',
      dataIndex: 'totalValue',
      key: 'totalValue',
      width: 140,
      align: 'center' as const,
      render: (value: string) => (
        <div style={{ textAlign: 'center' }}>
          <Text strong style={{ fontSize: '18px', color: '#DB8633', display: 'block' }}>
            {value}
          </Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>generated</Text>
        </div>
      ),
    }
  ];

  return (
    <Layout className="standard-layout">
      <AdminSidebar
        activeKey="referral-analytics"
        mobileVisible={mobileSidebarVisible}
        onMobileToggle={() => setMobileSidebarVisible(!mobileSidebarVisible)}
      />

      <Layout className="standard-main-content">
        <Header className="standard-header">
          <div className="header-left">
            <Title level={2} className="page-title">Referral Analytics</Title>
            <Text type="secondary" className="page-subtitle">Track and analyze referral performance</Text>
          </div>
          <div className="header-right">
            <Dropdown overlay={timeFilterMenu} trigger={['click']}>
              <Button className="time-filter-btn">
                {selectedTimeFilter} <DownOutlined />
              </Button>
            </Dropdown>
            <RangePicker 
              className="date-range-picker"
              onChange={(dates) => {
                if (dates) {
                  setDateRange([dates[0]?.format('YYYY-MM-DD') || '', dates[1]?.format('YYYY-MM-DD') || '']);
                }
              }}
            />
          </div>
        </Header>

        <Content className="standard-content">
          <Spin spinning={loading}>
            <div className="content-wrapper">
              {/* Overview Cards */}
              <Row gutter={[24, 24]} className="overview-cards">
              {referralOverviewData.map((card, index) => (
                <Col xs={24} sm={12} lg={8} xl={6} key={index}>
                  <Card className="overview-card">
                    <div className="card-content">
                      <div className="card-icon" style={{ color: card.color }}>
                        {card.icon}
                      </div>
                      <div className="card-stats">
                        <Statistic 
                          title={card.title} 
                          value={card.value}
                          valueStyle={{ color: card.color }}
                        />
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Main Content Tabs */}
            <Card className="main-content-card">
              <Tabs 
                activeKey={activeTab} 
                onChange={setActiveTab}
                className="referral-tabs"
                items={[
                  {
                    key: 'overview',
                    label: (
                      <span>
                        <BarChartOutlined style={{ marginRight: 8 }} />
                        Overview
                      </span>
                    ),
                    children: (
                      <div className="overview-content">
                        <Row gutter={[24, 24]}>
                          <Col span={16}>
                            <Card title="Monthly Referral Performance" className="chart-card">
                              <div className="monthly-performance">
                                {analyticsData?.monthlyTrends ? (
                                  analyticsData.monthlyTrends.map((trend: any, index: number) => {
                                    const monthName = new Date(trend.month + '-01').toLocaleDateString('en-US', { month: 'short' });
                                    const percentage = (trend.successful / trend.referrals) * 100;
                                    return (
                                      <div key={index} className="month-item">
                                        <div className="month-header">
                                          <Text strong>{monthName}</Text>
                                          <Text type="secondary" style={{ fontSize: '11px' }}>{trend.referrals} total</Text>
                                        </div>
                                        <Progress 
                                          percent={Math.round(percentage)} 
                                          strokeColor="#DB8633"
                                          format={() => `${trend.successful}`}
                                        />
                                        <Text type="secondary" style={{ fontSize: '11px' }}>
                                          ${trend.revenue.toLocaleString()}
                                        </Text>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <Text type="secondary">No monthly data available</Text>
                                )}
                              </div>
                            </Card>
                          </Col>
                          <Col span={8}>
                            <Card title="Referral Channels" className="chart-card">
                              <div className="channels-list">
                                {analyticsData?.referralSources ? (
                                  analyticsData.referralSources.map((source: any, index: number) => {
                                    const total = analyticsData.referralSources.reduce((sum: number, s: any) => sum + s.count, 0);
                                    const percentage = (source.count / total) * 100;
                                    return (
                                      <div key={index} style={{ marginBottom: '16px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                          <Text strong>{source.name}</Text>
                                          <Text type="secondary">{source.count}</Text>
                                        </div>
                                        <Progress 
                                          percent={Math.round(percentage)} 
                                          strokeColor="#DB8633"
                                          showInfo={false}
                                        />
                                      </div>
                                    );
                                  })
                                ) : (
                                  <Text type="secondary">No channel data available</Text>
                                )}
                              </div>
                            </Card>
                          </Col>
                        </Row>
                      </div>
                    )
                  },
                  {
                    key: 'top-referrers',
                    label: (
                      <span>
                        <CrownOutlined style={{ marginRight: 8 }} />
                        Top Referrers
                      </span>
                    ),
                    children: (
                      <div className="top-referrers-content">
                        {loading ? (
                          <Spin size="large" style={{ display: 'flex', justifyContent: 'center', padding: '40px' }} />
                        ) : error ? (
                          <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Text type="danger">{error}</Text>
                          </div>
                        ) : analyticsData?.topReferrers && analyticsData.topReferrers.length > 0 ? (
                          <Table 
                            dataSource={analyticsData.topReferrers.map((referrer: any, index: number) => {
                              const paid = referrer.successful_referrals ?? referrer.successful ?? 0;
                              const tiersFromApi =
                                typeof referrer.tiers_unlocked === 'number'
                                  ? referrer.tiers_unlocked
                                  : typeof referrer.tiersUnlocked === 'number'
                                    ? referrer.tiersUnlocked
                                    : paidRecognitionTiersUnlockedCount(Number(paid) || 0);
                              return {
                                ...referrer,
                                key: referrer.user_id || index,
                                rank: index + 1,
                                avatar: referrer.name ? referrer.name.split(' ').map((n: string) => n[0]).join('').toUpperCase() : 'U',
                                referrals: referrer.total_referrals || referrer.referrals || 0,
                                successful: paid,
                                conversionRate: referrer.conversion_rate ? `${referrer.conversion_rate}%` : '0%',
                                recognitionTiersUnlocked: tiersFromApi,
                                totalValue: referrer.total_value ? `$${referrer.total_value.toFixed(2)}` : '$0.00'
                              };
                            })} 
                            columns={referralColumns}
                            pagination={false}
                            className="referrers-table"
                            rowClassName="referrer-row"
                          />
                        ) : (
                          <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Text type="secondary">No referral data available. The backend may need to be updated to return referral data.</Text>
                          </div>
                        )}
                      </div>
                    )
                  },
                  {
                    key: 'all-donors',
                    label: (
                      <span>
                        <UserOutlined style={{ marginRight: 8 }} />
                        All Donors
                      </span>
                    ),
                    children: (
                      <div className="all-donors-content">
                        {/* Search */}
                        <Card style={{ marginBottom: 24 }}>
                          <Input
                            placeholder="Search by name, email, or user ID..."
                            prefix={<SearchOutlined />}
                            value={donorSearchText}
                            onChange={(e) => setDonorSearchText(e.target.value)}
                            allowClear
                            size="large"
                          />
                        </Card>

                        {/* Donors Table */}
                        <Card title={`All Donors (${filteredDonors.length})`}>
                          {donorsLoading ? (
                            <Spin size="large" style={{ display: 'flex', justifyContent: 'center', padding: '40px' }} />
                          ) : filteredDonors.length > 0 ? (
                            <Table
                              dataSource={filteredDonors.map((donor: any, index: number) => ({
                                ...donor,
                                key: donor.user_id || donor.id || index
                              }))}
                              columns={[
                                {
                                  title: 'Donor',
                                  key: 'donor',
                                  width: 250,
                                  render: (_: any, record: any) => (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                      <Avatar size={40} style={{ backgroundColor: '#DB8633' }}>
                                        {record.name ? record.name.split(' ').map((n: string) => n[0]).join('').toUpperCase() : 'U'}
                                      </Avatar>
                                      <div>
                                        <Text strong style={{ display: 'block' }}>{record.name || 'Unknown'}</Text>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>{record.email || 'N/A'}</Text>
                                      </div>
                                    </div>
                                  )
                                },
                                {
                                  title: 'Total Referrals',
                                  key: 'total_referrals',
                                  width: 120,
                                  align: 'center' as const,
                                  render: (_: any, record: any) => (
                                    <Text strong style={{ fontSize: '16px' }}>
                                      {record.referral_stats?.total_referrals || record.total_referrals || 0}
                                    </Text>
                                  )
                                },
                                {
                                  title: 'Paid referrals',
                                  key: 'successful',
                                  width: 120,
                                  align: 'center' as const,
                                  render: (_: any, record: any) => (
                                    <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                                      {record.referral_stats?.successful_referrals || record.successful_referrals || 0}
                                    </Text>
                                  )
                                },
                                {
                                  title: 'Conversion Rate',
                                  key: 'conversion_rate',
                                  width: 140,
                                  align: 'center' as const,
                                  render: (_: any, record: any) => {
                                    const rate = record.referral_stats?.conversion_rate || record.conversion_rate || 0;
                                    return (
                                      <Tag color={rate >= 70 ? 'green' : rate >= 50 ? 'orange' : 'red'}>
                                        {rate.toFixed(1)}%
                                      </Tag>
                                    );
                                  }
                                },
                                {
                                  title: 'Milestones',
                                  key: 'milestones',
                                  width: 120,
                                  align: 'center' as const,
                                  render: (_: any, record: any) => (
                                    <Space>
                                      {record.milestones?.length > 0 ? (
                                        <Tooltip
                                          title={record.milestones.map((m: any) => formatMilestoneSummary(m)).join(' · ')}
                                        >
                                          <Badge count={record.milestones.length} style={{ backgroundColor: '#DB8633' }}>
                                            <TrophyOutlined style={{ fontSize: '20px', color: '#DB8633' }} />
                                          </Badge>
                                        </Tooltip>
                                      ) : (
                                        <Text type="secondary">-</Text>
                                      )}
                                    </Space>
                                  )
                                },
                                {
                                  title: 'Recognition tiers',
                                  key: 'recognition_tiers',
                                  width: 130,
                                  align: 'center' as const,
                                  render: (_: any, record: any) => {
                                    const paid =
                                      record.referral_stats?.successful_referrals ??
                                      record.successful_referrals ??
                                      0;
                                    const n = paidRecognitionTiersUnlockedCount(Number(paid) || 0);
                                    return (
                                      <Text strong style={{ fontSize: '16px', color: '#DB8633' }}>
                                        {n}/{REFERRAL_TIER_COUNT}
                                      </Text>
                                    );
                                  }
                                },
                                {
                                  title: 'Website spotlight',
                                  key: 'website_spotlight',
                                  width: 130,
                                  align: 'center' as const,
                                  render: (_: any, record: any) => {
                                    const paid =
                                      record.referral_stats?.successful_referrals ??
                                      record.successful_referrals ??
                                      0;
                                    const eligible = Number(paid) >= 5;
                                    const featured = Boolean(record.featured_on_website);
                                    return (
                                      <Space direction="vertical" size={0} style={{ textAlign: 'center' }}>
                                        {eligible ? (
                                          <Tag color="blue">Eligible (≥5 paid)</Tag>
                                        ) : (
                                          <Text type="secondary">—</Text>
                                        )}
                                        {featured ? <Tag color="green">Featured on site</Tag> : null}
                                      </Space>
                                    );
                                  }
                                },
                                {
                                  title: 'Actions',
                                  key: 'actions',
                                  width: 150,
                                  fixed: 'right' as const,
                                  render: (_: any, record: any) => (
                                    <Space>
                                      <Tooltip title="Manual account credit only — referral rewards are recognition (badges / spotlight), not cash.">
                                        <Button
                                          type="primary"
                                          icon={<PlusOutlined />}
                                          size="small"
                                          onClick={() => openCreditGrantModal(record)}
                                          style={{
                                            backgroundColor: '#DB8633',
                                            borderColor: '#DB8633',
                                            color: '#ffffff'
                                          }}
                                        >
                                          Account credit
                                        </Button>
                                      </Tooltip>
                                    </Space>
                                  )
                                }
                              ]}
                              expandable={{
                                expandedRowKeys,
                                onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as React.Key[]),
                                expandedRowRender: (record: any) => (
                                  <div style={{ padding: '16px', background: '#fafafa', borderRadius: '8px' }}>
                                    <Row gutter={[24, 24]}>
                                      {/* Referrals */}
                                      <Col span={24}>
                                        <Card size="small" title={<><TeamOutlined style={{ marginRight: 8 }} /> Referrals</>} style={{ marginBottom: 16 }}>
                                          {record.referrals && record.referrals.length > 0 ? (
                                            <Table
                                              dataSource={record.referrals}
                                              columns={[
                                                {
                                                  title: 'Referred User',
                                                  dataIndex: 'referred_user_name',
                                                  key: 'referred_user_name',
                                                  render: (text: string) => text || 'N/A'
                                                },
                                                {
                                                  title: 'Referral Code',
                                                  dataIndex: 'referral_code',
                                                  key: 'referral_code',
                                                  render: (code: string) => <Text code>{code}</Text>
                                                },
                                                {
                                                  title: 'Status',
                                                  dataIndex: 'status',
                                                  key: 'status',
                                                  render: (status: string) => {
                                                    const statusConfig: any = {
                                                      pending: { color: 'orange', text: 'Pending' },
                                                      signed_up: { color: 'blue', text: 'Signed Up' },
                                                      payment_setup: { color: 'cyan', text: 'Payment Setup' },
                                                      paid: { color: 'green', text: 'Paid' },
                                                      cancelled: { color: 'red', text: 'Cancelled' }
                                                    };
                                                    const config = statusConfig[status] || { color: 'default', text: status };
                                                    return <Tag color={config.color}>{config.text}</Tag>;
                                                  }
                                                },
                                                {
                                                  title: 'Created',
                                                  dataIndex: 'created_at',
                                                  key: 'created_at',
                                                  render: (date: string) => date ? new Date(date).toLocaleDateString() : 'N/A'
                                                },
                                                {
                                                  title: 'Paid',
                                                  dataIndex: 'paid_at',
                                                  key: 'paid_at',
                                                  render: (date: string) => date ? new Date(date).toLocaleDateString() : '-'
                                                }
                                              ]}
                                              pagination={false}
                                              size="small"
                                            />
                                          ) : (
                                            <Empty description="No referrals" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                          )}
                                        </Card>
                                      </Col>

                                      {/* Milestones */}
                                      <Col span={12}>
                                        <Card size="small" title={<><TrophyOutlined style={{ marginRight: 8 }} /> Milestones</>}>
                                          {record.milestones && record.milestones.length > 0 ? (
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                              {record.milestones.map((milestone: any, idx: number) => {
                                                const th = milestoneToPaidFriendsThreshold(milestone);
                                                const title =
                                                  th != null
                                                    ? recognitionLabelForThreshold(th)
                                                    : String(milestone.milestone_type || 'Milestone').replace(/_/g, ' ');
                                                const desc = milestone.reward_description
                                                  ? String(milestone.reward_description)
                                                  : null;
                                                const badgeName = milestone.badge_name
                                                  ? String(milestone.badge_name)
                                                  : null;
                                                const rewardVal = Number(milestone.reward_value) || 0;
                                                return (
                                                  <div key={idx} style={{ padding: '8px', background: '#fff', borderRadius: '4px', border: '1px solid #f0f0f0' }}>
                                                    <Text strong>{title}</Text>
                                                    {badgeName ? (
                                                      <>
                                                        <br />
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                          Badge: <Text code>{badgeName}</Text>
                                                        </Text>
                                                      </>
                                                    ) : null}
                                                    <br />
                                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                                      Unlocked:{' '}
                                                      {milestone.unlocked_at
                                                        ? new Date(milestone.unlocked_at).toLocaleDateString()
                                                        : 'N/A'}
                                                    </Text>
                                                    {desc ? (
                                                      <>
                                                        <br />
                                                        <Text style={{ fontSize: '12px' }}>{desc}</Text>
                                                      </>
                                                    ) : null}
                                                    {rewardVal > 0 ? (
                                                      <>
                                                        <br />
                                                        <Text style={{ color: '#DB8633', fontWeight: 600 }}>
                                                          Credit (legacy): ${rewardVal.toFixed(2)}
                                                        </Text>
                                                      </>
                                                    ) : null}
                                                  </div>
                                                );
                                              })}
                                            </Space>
                                          ) : (
                                            <Empty description="No milestones unlocked" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                          )}
                                        </Card>
                                      </Col>

                                      {/* Account credits — referral program is recognition-only; amounts are legacy/manual */}
                                      <Col span={12}>
                                        <Card
                                          size="small"
                                          title={<><DollarOutlined style={{ marginRight: 8 }} /> Account credits</>}
                                        >
                                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>
                                            Referrals no longer earn donor cash credits. Balances below are manual or historical only.
                                          </Text>
                                          <Space direction="vertical" style={{ width: '100%' }}>
                                            <div>
                                              <Text type="secondary">Active: </Text>
                                              <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>
                                                ${(record.referral_stats?.active_credits || record.active_credits || 0).toFixed(2)}
                                              </Text>
                                            </div>
                                            <div>
                                              <Text type="secondary">Total recorded: </Text>
                                              <Text strong style={{ color: '#DB8633', fontSize: '16px' }}>
                                                ${(record.referral_stats?.total_credits_earned || record.total_credits_earned || 0).toFixed(2)}
                                              </Text>
                                            </div>
                                            <div>
                                              <Text type="secondary">Expired: </Text>
                                              <Text strong style={{ color: '#ff4d4f', fontSize: '16px' }}>
                                                ${(record.referral_stats?.expired_credits || record.expired_credits || 0).toFixed(2)}
                                              </Text>
                                            </div>
                                          </Space>
                                        </Card>
                                      </Col>
                                    </Row>
                                  </div>
                                )
                              }}
                              pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showTotal: (total) => `Total ${total} donors`
                              }}
                              scroll={{ x: 1400 }}
                            />
                          ) : (
                            <Empty description="No donors found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                          )}
                        </Card>
                      </div>
                    )
                  },
                  {
                    key: 'invitations',
                    label: (
                      <span>
                        <MailOutlined style={{ marginRight: 8 }} />
                        Invitation Management
                      </span>
                    ),
                    children: (
                      <div className="invitations-content">
                        {/* Statistics Cards */}
                        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                          <Col xs={24} sm={12} lg={6}>
                            <Card>
                              <Statistic 
                                title="Pending Invitations" 
                                value={invitationStats.pending} 
                                valueStyle={{ color: '#DB8633' }}
                              />
                            </Card>
                          </Col>
                          <Col xs={24} sm={12} lg={6}>
                            <Card>
                              <Statistic 
                                title="Accepted Today" 
                                value={invitationStats.acceptedToday} 
                                valueStyle={{ color: '#DB8633' }}
                              />
                            </Card>
                          </Col>
                          <Col xs={24} sm={12} lg={6}>
                            <Card>
                              <Statistic 
                                title="Total Accepted" 
                                value={invitationStats.accepted} 
                                valueStyle={{ color: '#52c41a' }}
                              />
                            </Card>
                          </Col>
                          <Col xs={24} sm={12} lg={6}>
                            <Card>
                              <Statistic 
                                title="Expired This Week" 
                                value={invitationStats.expiredThisWeek} 
                                valueStyle={{ color: '#324E58' }}
                              />
                            </Card>
                          </Col>
                        </Row>

                        {/* Action Buttons */}
                        <Card style={{ marginBottom: 24 }}>
                          <Space wrap>
                            <Button 
                              type="primary"
                              icon={<UserAddOutlined />} 
                              onClick={handleSendNewInvitations}
                              style={{
                                backgroundColor: '#DB8633',
                                borderColor: '#DB8633',
                                color: '#ffffff'
                              }}
                            >
                              Send New Invitations
                            </Button>
                            <Button 
                              icon={<MailOutlined />}
                              onClick={handleResendPending}
                              loading={resendPendingLoading}
                              disabled={invitationStats.pending === 0}
                              style={{
                                backgroundColor: invitationStats.pending > 0 ? '#DB8633' : undefined,
                                borderColor: invitationStats.pending > 0 ? '#DB8633' : undefined,
                                color: invitationStats.pending > 0 ? '#ffffff' : undefined
                              }}
                            >
                              Resend Pending ({invitationStats.pending})
                            </Button>
                            <Button 
                              icon={<LinkOutlined />}
                              onClick={handleGenerateReferralLinks}
                              style={{
                                backgroundColor: '#DB8633',
                                borderColor: '#DB8633',
                                color: '#ffffff'
                              }}
                            >
                              Generate Referral Links
                            </Button>
                          </Space>
                        </Card>

                        {/* Filters and Search */}
                        <Card style={{ marginBottom: 24 }}>
                          <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} sm={12} md={8}>
                              <Select
                                placeholder="Filter by Status"
                                value={invitationStatusFilter}
                                onChange={setInvitationStatusFilter}
                                style={{ width: '100%' }}
                              >
                                <Option value="all">All Statuses</Option>
                                <Option value="pending">Pending</Option>
                                <Option value="signed_up">Signed Up</Option>
                                <Option value="payment_setup">Payment Setup</Option>
                                <Option value="paid">Paid</Option>
                                <Option value="cancelled">Cancelled</Option>
                              </Select>
                            </Col>
                            <Col xs={24} sm={12} md={16}>
                              <Input
                                placeholder="Search by email, referral code, or referrer name..."
                                prefix={<SearchOutlined />}
                                value={invitationSearchText}
                                onChange={(e) => setInvitationSearchText(e.target.value)}
                                allowClear
                              />
                            </Col>
                          </Row>
                        </Card>

                        {/* Invitations Table */}
                        <Card title={`All Invitations (${filteredInvitations.length})`}>
                          {invitationsLoading ? (
                            <Spin size="large" style={{ display: 'flex', justifyContent: 'center', padding: '40px' }} />
                          ) : filteredInvitations.length > 0 ? (
                            <Table 
                              dataSource={filteredInvitations.map((inv: any, index: number) => ({
                                ...inv,
                                key: inv.id || inv.referral_code || index
                              }))}
                              columns={invitationColumns}
                              pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showTotal: (total) => `Total ${total} invitations`
                              }}
                              scroll={{ x: 1200 }}
                              size="small"
                            />
                          ) : (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                              <Text type="secondary">
                                {invitationSearchText || invitationStatusFilter !== 'all' 
                                  ? 'No invitations match your filters.' 
                                  : 'No invitations found. Invitations will appear here when users share referral links.'}
                              </Text>
                            </div>
                          )}
                        </Card>
                      </div>
                    )
                  }
                ]}
              />
            </Card>
            </div>
          </Spin>
        </Content>
      </Layout>

      {/* Generate Referral Links Modal */}
      <Modal
        title={
          <Space>
            <LinkOutlined />
            <span>Generate Referral Link</span>
          </Space>
        }
        open={generateReferralModalVisible}
        onCancel={() => setGenerateReferralModalVisible(false)}
        footer={null}
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Select a donor to copy their referral link. Friends who become paying donors unlock recognition tiers (badges /
            spotlight), not cash credits.
          </Text>
          <Select
            placeholder="Search and select a donor..."
            style={{ width: '100%' }}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={filteredDonors.map((d: any) => ({
              value: d.user_id || d.id,
              label: `${d.name || d.email} (${d.email})`
            }))}
            onChange={(userId) => {
              const donor = filteredDonors.find((d: any) => (d.user_id || d.id) === userId);
              if (donor) {
                const link = `${typeof window !== 'undefined' ? window.location.origin : ''}/signup?ref=${userId}`;
                navigator.clipboard.writeText(link).then(() => {
                  message.success('Referral link copied to clipboard!');
                });
              }
            }}
            notFoundContent={donorsLoading ? <Spin size="small" /> : <Empty description="No donors found" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          />
        </div>
        {filteredDonors.length > 0 && (
          <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Tip: Select a donor above to copy their link. Or copy from the Referral Leaders table.
            </Text>
          </div>
        )}
      </Modal>

      {/* Manual account credit — not part of referral recognition */}
      <Modal
        title={
          <Space>
            <DollarOutlined />
            <span>Grant account credit — {selectedDonor?.name || 'Donor'}</span>
          </Space>
        }
        open={creditGrantModalVisible}
        onCancel={() => {
          setCreditGrantModalVisible(false);
          creditGrantForm.resetFields();
          setSelectedDonor(null);
        }}
        footer={null}
        width={600}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Use for manual goodwill or billing adjustments only. The referral program awards recognition (tiers at 1, 3, and 5
          paid referrals), not automatic credits.
        </Text>
        <Form
          form={creditGrantForm}
          layout="vertical"
          requiredMark="optional"
          onFinish={handleCreditGrant}
          initialValues={{
            expiresInDays: 90
          }}
        >
          <Form.Item
            label="Credit amount"
            name="amount"
            rules={[
              { required: true, message: 'Please enter credit amount' },
              { type: 'number', min: 0.01, message: 'Amount must be greater than 0' }
            ]}
          >
            <InputNumber
              prefix="$"
              style={{ width: '100%' }}
              placeholder="Enter credit amount"
              min={0.01}
              step={0.01}
              precision={2}
            />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <TextArea
              rows={3}
              placeholder="Reason for this manual credit (e.g. support escalation, promo)..."
            />
          </Form.Item>

          <Form.Item
            label="Expires In (Days)"
            name="expiresInDays"
            rules={[{ required: true, message: 'Please enter expiration days' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="90"
              min={1}
              max={365}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<CheckCircleOutlined />}
                style={{
                  backgroundColor: '#DB8633',
                  borderColor: '#DB8633',
                  color: '#ffffff'
                }}
              >
                Grant account credit
              </Button>
              <Button
                onClick={() => {
                  setCreditGrantModalVisible(false);
                  creditGrantForm.resetFields();
                  setSelectedDonor(null);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      {/* Send New Invitations Modal */}
      <Modal
        title={
          <Space>
            <MailOutlined />
            <span>Send New Invitations</span>
          </Space>
        }
        open={sendInviteModalVisible}
        onCancel={() => {
          setSendInviteModalVisible(false);
          sendInviteForm.resetFields();
        }}
        onOk={handleSendInviteSubmit}
        okText="Send Invitations"
        confirmLoading={sendInviteLoading}
        width={480}
      >
        <Form form={sendInviteForm} layout="vertical" requiredMark="optional">
          <Form.Item
            name="emails"
            label="Email Addresses"
            rules={[{ required: true, message: 'Please enter at least one email address' }]}
            extra="Separate multiple emails with commas, semicolons, or new lines."
          >
            <Input.TextArea
              rows={5}
              placeholder="e.g. alice@example.com, bob@example.com"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default ReferralAnalytics;
